import { useEffect, useRef, useState, type FormEvent } from 'react'
import { AnimatePresence, motion } from 'motion/react'
import { ArrowUp, Search, Sparkles, X } from 'lucide-react'
import { apiPost } from '@/api/axios-client'
import { cn } from '@/lib/utils'
import { playIslandSound } from './sounds'

/**
 * Ask, inside the Dynamic Island: questions about your own work, answered by the LLM with your
 * live context (open work, assigned L3 tickets and their SLA state, counts, the page you're on).
 *
 * The API returns the finished answer (the backend sits behind a buffering gateway), and the
 * panel streams it onto the screen word by word with a caret, the way ChatGPT does. The
 * conversation lasts for the island session and the last few turns go back as context.
 */

interface Message {
  id: number
  role: 'user' | 'assistant'
  content: string
}

const SUGGESTIONS = ['What should I do first?', 'Which L3 tickets are breached?', 'What’s due today?', 'Summarize my week']
const SPRING = { type: 'spring', stiffness: 420, damping: 32 } as const

export function AskPanel({ pageLabel, onClose, reduce }: { pageLabel: string; onClose: () => void; reduce: boolean | null }) {
  const [messages, setMessages] = useState<Message[]>([])
  const [input, setInput] = useState('')
  const [pending, setPending] = useState(false)
  const [focused, setFocused] = useState(true)
  const inputRef = useRef<HTMLInputElement>(null)
  const scrollRef = useRef<HTMLDivElement>(null)
  const nextId = useRef(1)

  useEffect(() => {
    const t = window.setTimeout(() => inputRef.current?.focus(), 60)
    return () => window.clearTimeout(t)
  }, [])

  const scrollToEnd = () => {
    const el = scrollRef.current
    if (el) el.scrollTo({ top: el.scrollHeight, behavior: reduce ? 'auto' : 'smooth' })
  }
  useEffect(scrollToEnd, [messages.length, pending]) // eslint-disable-line react-hooks/exhaustive-deps

  const ask = async (question: string) => {
    const q = question.trim()
    if (!q || pending) return
    playIslandSound('tap')
    const history = messages.slice(-8).map(({ role, content }) => ({ role, content }))
    setMessages((m) => [...m, { id: nextId.current++, role: 'user', content: q }])
    setInput('')
    setPending(true)
    try {
      const res = await apiPost<{ answer: string }>('island/ask', { question: q, page: pageLabel, history })
      setMessages((m) => [...m, { id: nextId.current++, role: 'assistant', content: res.answer }])
    } catch (err) {
      const status = (err as { response?: { status?: number } })?.response?.status
      setMessages((m) => [...m, {
        id: nextId.current++,
        role: 'assistant',
        content: status === 429 ? 'That’s a lot of questions this hour. Try again in a little while.' : 'I couldn’t answer that just now. Try again in a moment.',
      }])
    } finally {
      setPending(false)
      window.setTimeout(() => inputRef.current?.focus(), 30)
    }
  }

  const onSubmit = (e: FormEvent) => {
    e.preventDefault()
    void ask(input)
  }

  return (
    <div
      className="flex max-h-[min(32rem,calc(100dvh-7rem))] flex-col"
      onClick={(e) => e.stopPropagation()}
      onKeyDown={(e) => {
        if (e.key === 'Escape') {
          e.stopPropagation()
          onClose()
        }
      }}
    >
      <div className="flex items-center justify-between gap-2 px-5 pt-4">
        <p className="flex items-center gap-1.5 text-xs font-medium text-white/55"><Sparkles className="size-3.5 text-ios-green" /> Ask about your work</p>
        <button type="button" aria-label="Close Ask" onClick={onClose} className="grid size-7 place-items-center rounded-full text-white/50 transition-colors hover:bg-white/10 hover:text-white">
          <X className="size-4" />
        </button>
      </div>

      <div ref={scrollRef} className="scrollbar-none min-h-0 flex-1 overflow-y-auto overscroll-contain px-5 pt-3">
        {messages.length === 0 && !pending ? (
          <div className="flex flex-wrap gap-1.5 pb-1">
            {SUGGESTIONS.map((s, i) => (
              <motion.button
                key={s}
                type="button"
                onClick={() => void ask(s)}
                initial={reduce ? false : { opacity: 0, y: 8, scale: 0.96 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                transition={{ ...SPRING, delay: 0.05 + i * 0.05 }}
                whileTap={{ scale: 0.96 }}
                className="rounded-full border border-white/15 bg-white/[0.06] px-3 py-1.5 text-xs text-white/85 transition-colors hover:border-white/30 hover:bg-white/12"
              >
                {s}
              </motion.button>
            ))}
          </div>
        ) : null}

        <div className="grid gap-3 pb-2">
          {messages.map((m, i) => (
            <motion.div
              key={m.id}
              initial={reduce ? false : { opacity: 0, y: 10, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              transition={SPRING}
              className={cn(m.role === 'user' ? 'ml-auto max-w-[85%] rounded-2xl rounded-br-md bg-white px-3.5 py-2 text-sm font-medium text-black' : 'text-[15px] leading-relaxed text-white/90')}
            >
              {m.role === 'assistant' ? <StreamText text={m.content} animate={!reduce && i === messages.length - 1} onTick={scrollToEnd} /> : m.content}
            </motion.div>
          ))}
          <AnimatePresence>
            {pending ? (
              <motion.div key="thinking" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="flex items-center gap-1.5 py-1" aria-label="Thinking">
                {[0, 1, 2].map((d) => (
                  <motion.span
                    key={d}
                    className="size-1.5 rounded-full bg-white/70"
                    animate={reduce ? undefined : { opacity: [0.25, 1, 0.25], y: [0, -3, 0] }}
                    transition={{ duration: 0.9, repeat: Infinity, delay: d * 0.15, ease: 'easeInOut' }}
                  />
                ))}
              </motion.div>
            ) : null}
          </AnimatePresence>
        </div>
      </div>

      <form onSubmit={onSubmit} className="px-4 pb-4 pt-2">
        <motion.div
          animate={{ boxShadow: focused ? '0 0 0 2px var(--color-ios-green), 0 8px 24px -10px var(--color-ios-green)' : '0 0 0 1px color-mix(in srgb, white 12%, transparent)' }}
          transition={{ duration: 0.25, ease: [0.32, 0.72, 0, 1] }}
          className="flex items-center gap-2 rounded-2xl bg-white/[0.08] py-1.5 pl-3 pr-1.5"
        >
          <Search className="size-4 shrink-0 text-white/60" strokeWidth={2.5} />
          <input
            ref={inputRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onFocus={() => setFocused(true)}
            onBlur={() => setFocused(false)}
            placeholder={messages.length ? 'Ask a follow-up…' : 'Ask anything about your work…'}
            maxLength={500}
            aria-label="Ask a question"
            className="min-w-0 flex-1 bg-transparent py-1.5 text-sm font-medium text-white placeholder:text-white/40 focus:outline-none"
          />
          <motion.button
            type="submit"
            disabled={!input.trim() || pending}
            whileTap={{ scale: 0.9 }}
            animate={{ scale: input.trim() ? 1 : 0.85, opacity: input.trim() ? 1 : 0.4 }}
            transition={SPRING}
            aria-label="Send"
            className="grid size-8 shrink-0 place-items-center rounded-full bg-white text-black disabled:cursor-default"
          >
            <ArrowUp className="size-4" strokeWidth={3} />
          </motion.button>
        </motion.div>
      </form>
    </div>
  )
}

/** Reveals the answer a few words at a time with a blinking caret, then settles to plain text. */
function StreamText({ text, animate, onTick }: { text: string; animate: boolean; onTick: () => void }) {
  const words = text.split(/(\s+)/)
  const [shown, setShown] = useState(animate ? 0 : words.length)
  useEffect(() => {
    if (!animate) return
    let i = 0
    const t = window.setInterval(() => {
      // Two to four tokens per tick reads like a fast stream without feeling mechanical.
      i = Math.min(words.length, i + 2 + Math.floor(Math.random() * 3))
      setShown(i)
      onTick()
      if (i >= words.length) window.clearInterval(t)
    }, 38)
    return () => window.clearInterval(t)
    // eslint-disable-next-line react-hooks/exhaustive-deps -- runs once per answer
  }, [text])
  const done = shown >= words.length
  return (
    <span className="whitespace-pre-wrap">
      {words.slice(0, shown).join('')}
      {!done ? <span className="ml-0.5 inline-block h-[1.05em] w-[2px] translate-y-[2px] animate-pulse rounded-full bg-ios-green" aria-hidden /> : null}
    </span>
  )
}
